import React, { useEffect, useId } from "react";
import { createPortal } from "react-dom";
import {
  Backdrop,
  DialogContainer,
  DialogPanel,
  DialogHeader,
  DialogTitle,
  CloseButton,
  DialogContent,
  DialogFooter,
} from "./styles";
import { AlertDialogProps } from "./types";

/**
 * Componente AlertDialog
 *
 * Um diálogo modal centralizado com suporte a fechamento via backdrop,
 * tecla Escape e bloqueio de scroll. Em telas pequenas, funciona como
 * bottom sheet.
 *

 */
export const AlertDialog: React.FC<AlertDialogProps> = ({
  isOpen,
  title,
  onClose,
  children,
  footer,
}) => {
  const titleId = useId();

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return createPortal(
    <>
      <Backdrop onClick={handleBackdropClick} />
      <DialogContainer>
        <DialogPanel role="dialog" aria-modal="true" aria-labelledby={titleId}>
          <DialogHeader>
            <DialogTitle id={titleId}>{title}</DialogTitle>
            <CloseButton onClick={onClose} aria-label="Fechar" type="button">
              ×
            </CloseButton>
          </DialogHeader>
          <DialogContent>{children}</DialogContent>
          {footer && <DialogFooter>{footer}</DialogFooter>}
        </DialogPanel>
      </DialogContainer>
    </>,
    document.body,
  );
};
